import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Box, Paper, Switch, TextField, FormControl, RadioGroup,
  FormControlLabel, Radio, Tooltip, Button, CircularProgress,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { WithContext as ReactTags } from 'react-tag-input';
import { useCreateAlert } from '../hooks/useAlerts';
import { useNotification } from '../context/NotificationContext';
import PortalPicker from './PortalPicker';

const KeyCodes = { comma: 188, enter: 13 };
const delimiters = [KeyCodes.comma, KeyCodes.enter];

const SectionHeader = ({ title, tooltip }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, mt: 4 }}>
    <Typography
      variant="h6"
      sx={{ fontWeight: 600, color: '#2c3e50', borderBottom: '2px solid #0079bf', pb: 0.5, fontSize: '1rem' }}
    >
      {title}
    </Typography>
    {tooltip && (
      <Tooltip title={tooltip} placement="right">
        <HelpOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      </Tooltip>
    )}
  </Box>
);

export default function AlertCreate() {
  const navigate = useNavigate();
  const createAlert = useCreateAlert();
  const { showError, showWarning } = useNotification();

  const nameRef = useRef(null);
  useEffect(() => { nameRef.current?.focus(); }, []);

  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [selectedPortals, setSelectedPortals] = useState([]);
  const [termTags, setTermTags] = useState([]);
  const [excludingTermTags, setExcludingTermTags] = useState([]);
  const [emailTags, setEmailTags] = useState([]);
  const [notificationMode, setNotificationMode] = useState('immediate');
  const [saving, setSaving] = useState(false);

  useEffect(() => { document.title = 'Novi alert | Media Scanner'; }, []);

  const isFormValid = name.trim() && termTags.length > 0 && selectedPortals.length > 0 && emailTags.length > 0;

  const handleSave = async () => {
    if (!name.trim()) { showWarning('Naziv alerta je obavezno polje'); return; }
    if (termTags.length === 0) { showWarning('Dodajte barem jedan pojam za pretragu'); return; }
    if (selectedPortals.length === 0) { showWarning('Odaberite barem jedan portal'); return; }
    if (emailTags.length === 0) { showWarning('Dodajte barem jednu email adresu'); return; }

    setSaving(true);
    createAlert.mutate(
      {
        alert_name: name.trim(),
        is_active: isActive,
        list_portal_ids: selectedPortals.map(p => p.id),
        list_term_names: termTags.map(t => t.text),
        list_excluding_term_names: excludingTermTags.map(t => t.text),
        list_email_addresses: emailTags.map(t => t.text),
        notification_mode: notificationMode,
      },
      {
        onSuccess: () => navigate('/alarms'),
        onError: () => { showError('Greška pri kreiranju alerta. Pokušajte ponovo.'); setSaving(false); },
      }
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <Box sx={{ maxWidth: 800, mx: 'auto', px: 2, py: 3 }}>
        {/* Page header */}
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50', mb: 3 }}>
          Novi alert
        </Typography>

        <Paper sx={{ p: 3, borderRadius: 2 }}>
          {/* Name & Status */}
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50', mb: 0.5 }}>Naziv</Typography>
          <TextField
            fullWidth
            inputRef={nameRef}
            placeholder="Unesi naziv alerta"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!name.trim()}
            helperText={!name.trim() ? 'Naziv je obavezno polje' : ''}
            size="small"
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#2c3e50' }}>Status:</Typography>
            <Typography
              variant="caption"
              sx={{
                px: 1, py: 0.25, borderRadius: 1, fontWeight: 600,
                backgroundColor: isActive ? '#e8f5e9' : '#f5f5f5',
                color: isActive ? '#27ae60' : '#999',
              }}
            >
              {isActive ? 'Aktivan' : 'Neaktivan'}
            </Typography>
            <Switch size="small" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="primary" />
          </Box>

          {/* Notification mode */}
          <SectionHeader title="Način obavještavanja" />
          <FormControl>
            <RadioGroup row value={notificationMode} onChange={(e) => setNotificationMode(e.target.value)}>
              <FormControlLabel value="immediate" control={<Radio />} label="Odmah (svaki pronalazak)" />
              <FormControlLabel value="daily_digest" control={<Radio />} label="Dnevni sažetak (08:30 i 15:30)" />
            </RadioGroup>
          </FormControl>

          {/* Terms */}
          <SectionHeader title="Pojmovi za alert" />
          <ReactTags
            tags={termTags}
            suggestions={[]}
            delimiters={delimiters}
            handleDelete={(i) => setTermTags(termTags.filter((_, idx) => idx !== i))}
            handleAddition={(tag) => setTermTags([...termTags, tag])}
            inputFieldPosition="bottom"
            placeholder="Unesi pojam, zatim zarez ili Enter"
            autocomplete
            editable
          />

          {/* Excluding terms */}
          <SectionHeader
            title="Isključujući pojmovi"
            tooltip="Članci koji sadrže ove pojmove neće biti prikazani u rezultatima pretrage"
          />
          <ReactTags
            tags={excludingTermTags}
            suggestions={[]}
            delimiters={delimiters}
            handleDelete={(i) => setExcludingTermTags(excludingTermTags.filter((_, idx) => idx !== i))}
            handleAddition={(tag) => setExcludingTermTags([...excludingTermTags, tag])}
            inputFieldPosition="bottom"
            placeholder="Unesi pojam za isključivanje, zatim zarez ili Enter"
            autocomplete
            editable
          />

          {/* Portals */}
          <SectionHeader title="Portali" />
          <PortalPicker value={selectedPortals} onChange={setSelectedPortals} showChips={false} />

          {/* Email addresses */}
          <SectionHeader title="Email adrese za dostavu alerta" />
          <ReactTags
            tags={emailTags}
            suggestions={[]}
            delimiters={delimiters}
            handleDelete={(i) => setEmailTags(emailTags.filter((_, idx) => idx !== i))}
            handleAddition={(tag) => setEmailTags([...emailTags, tag])}
            inputFieldPosition="bottom"
            placeholder="Unesi email adresu, zatim zarez ili Enter"
            autocomplete
            editable
          />

          {/* Action buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 4, pt: 3, borderTop: '1px solid #eee' }}>
            <Button variant="outlined" onClick={() => navigate('/alarms')}>
              Odustani
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !isFormValid}
              startIcon={saving ? <CircularProgress size={16} /> : null}
            >
              {saving ? 'Kreiranje...' : 'Kreiraj alert'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
